import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { reportsAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import { EmptyState, panelCls } from '../../components/ui';
import { PRODUCT_COLOR, PRODUCT_LABEL, PRODUCTS, ProductBadge, StackedBars } from '../../components/products';
import { nyTime } from '../../lib/nyTime';

// Reports (MMS_POINTS_HUB_PLAN F1 + F2). Points by product, then everything the
// admin used to read in My Account → Reports, served from the same brand DB.

const PRESETS = [
  { key: '1M', label: 'This month', months: 1 },
  { key: '6M', label: 'Last 6 months', months: 6 },
  { key: '1Y', label: 'Last 12 months', months: 12 },
  { key: 'ALL', label: 'All time', months: 0 },
];

function rangeFor(preset) {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  if (preset.months === 0) return { from: '2024-01-01', to };
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (preset.months - 1), 1));
  return { from: d.toISOString().slice(0, 10), to };
}

const fmt = (n) => Number(n || 0).toLocaleString();
const when = (iso) => nyTime(iso);

function Tile({ label, value, sub }) {
  return (
    <div className={`${panelCls} p-4`}>
      <div className="text-[10.5px] font-semibold uppercase tracking-wide text-mms-faint">{label}</div>
      <div className="mt-1.5 text-xl font-bold mms-num text-mms-text">{value}</div>
      {sub ? <div className="mt-0.5 text-xs text-mms-muted">{sub}</div> : null}
    </div>
  );
}

function Section({ title, children, right }) {
  return (
    <section className={`${panelCls} p-4`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-mms-text">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

function Table({ head, rows, empty }) {
  if (!rows.length) return <p className="text-sm text-mms-muted">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-mms-border text-left text-xs text-mms-faint">
            {head.map((h, i) => <th key={i} className={`px-3 py-2 font-medium ${h.right ? 'text-right' : ''}`}>{h.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, i) => (
            <tr key={i} className="border-b border-mms-border/60 last:border-0">
              {cells.map((c, j) => <td key={j} className={`px-3 py-2 ${head[j]?.right ? 'text-right mms-num' : ''}`}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ReportsPage() {
  const [preset, setPreset] = useState(PRESETS[1]);
  const [points, setPoints] = useState(null);
  const [rep, setRep] = useState(null);

  useEffect(() => {
    const params = rangeFor(preset);
    setPoints(null);
    setRep(null);
    reportsAPI.points(params).then(({ data }) => setPoints(data)).catch(() => toast.error(t('Could not load the points report')));
    reportsAPI.overview(params).then(({ data }) => setRep(data)).catch(() => toast.error(t('Could not load the activity report')));
  }, [preset]);

  const productKeys = useMemo(() => PRODUCTS.map((p) => ({ key: p, label: t(PRODUCT_LABEL[p]), color: PRODUCT_COLOR[p] })), []);
  const activityKeys = [
    { key: 'reads', label: t('Reads'), color: 'var(--mms-info)' },
    { key: 'comments', label: t('Comments'), color: '#8b5cf6' },
    { key: 'shares', label: t('Shares'), color: 'var(--mms-warning)' },
    { key: 'lessons', label: t('Lessons'), color: 'var(--mms-success)' },
  ];
  const peopleKeys = [
    { key: 'logins', label: t('Members who logged in'), color: 'var(--mms-sidebar)' },
    { key: 'registrations', label: t('Registrations'), color: 'var(--mms-success)' },
  ];
  const k = rep?.kpis || {};
  const a = rep?.mms_actions || {};

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-mms-text">{t('Reports')}</h1>
          <p className="mt-1 text-[12.5px] text-mms-muted">{t('Points by product and member activity across every product.')}</p>
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label={t('Date range')}>
          {PRESETS.map((p) => (
            <button key={p.key} type="button" onClick={() => setPreset(p)} aria-pressed={preset.key === p.key}
              className={`rounded-md border px-3 py-1.5 text-xs ${preset.key === p.key ? 'border-[var(--mms-accent)] text-mms-text bg-mms-surface' : 'border-mms-border text-mms-muted hover:text-mms-text'}`}>
              {t(p.label)}
            </button>
          ))}
        </div>
      </div>

      {/* ── Points by product ── */}
      {points === null ? (
        <p className="text-sm text-mms-muted">{t('Loading…')}</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            {points.products.map((p) => (
              <div key={p.product} className={`${panelCls} p-4`}>
                <ProductBadge product={p.product} />
                <div className="mt-2 text-xl font-bold mms-num text-mms-text">{fmt(p.points)}</div>
                <div className="text-xs text-mms-muted mms-num">{t('points')} · {fmt(p.xp)} XP</div>
                <div className="mt-1 text-xs text-mms-faint mms-num">{fmt(p.members)} {t('members')} · {fmt(p.events)} {t('actions')}</div>
              </div>
            ))}
          </div>
          <Section title={t('Points issued per month, by product')}>
            {points.series.every((r) => PRODUCTS.every((p) => !r[p])) ? (
              <EmptyState>{t('No points issued in this range.')}</EmptyState>
            ) : (
              <StackedBars rows={points.series} keys={productKeys} ariaLabel={t('Stacked bars of points issued per month, one colour per product.')} />
            )}
          </Section>
          <div className="grid gap-5 lg:grid-cols-2">
            <Section title={t('Top members')}>
              <Table
                head={[{ label: t('Member') }, { label: t('ID') }, { label: t('Awards'), right: true }, { label: t('Points'), right: true }]}
                rows={points.top_members.map((m) => [m.name, m.membership_id || '—', fmt(m.awards), fmt(m.points)])}
                empty={t('No points issued in this range.')} />
            </Section>
            <Section title={t('Top actions')}>
              <Table
                head={[{ label: t('Action') }, { label: t('Product') }, { label: t('Awards'), right: true }, { label: t('Points'), right: true }]}
                rows={points.top_actions.map((x) => [x.name, <ProductBadge product={x.product} />, fmt(x.awards), fmt(x.points)])}
                empty={t('No points issued in this range.')} />
            </Section>
          </div>
        </>
      )}

      {/* ── Member activity (My Account → Reports, served here) ── */}
      <h2 className="pt-2 text-base font-semibold text-mms-text">{t('Member activity')}</h2>
      {rep === null ? (
        <p className="text-sm text-mms-muted">{t('Loading…')}</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <Tile label={t('Members')} value={fmt(k.total_members)} sub={t('today')} />
            <Tile label={t('Logged in')} value={fmt(k.logins)} sub={t('unique members')} />
            <Tile label={t('Registrations')} value={fmt(k.registrations)} />
            <Tile label={t('Pre-registered free')} value={fmt(k.pre_registered_free)} sub={t('today')} />
            <Tile label={t('Pre-registered paid')} value={fmt(k.pre_registered_paid)} sub={t('today')} />
            <Tile label={t('KMS purchases')} value={fmt(k.kms_purchases)} />
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
            <Tile label={t('Reads')} value={fmt(a.reads)} />
            <Tile label={t('Comments')} value={fmt(a.comments)} />
            <Tile label={t('Shares')} value={fmt(a.shares)} />
            <Tile label={t('Affiliations')} value={fmt(a.affiliations)} />
            <Tile label={t('Lessons')} value={fmt(a.lessons)} />
            <Tile label={t('Courses')} value={fmt(a.courses)} />
            <Tile label={t('Active members')} value={fmt(a.unique_actors)} />
          </div>
          <div className="grid gap-5 lg:grid-cols-2">
            <Section title={t('Actions per month')}>
              <StackedBars rows={rep.series} keys={activityKeys} ariaLabel={t('Stacked bars of reads, comments, shares and lessons per month.')} />
            </Section>
            <Section title={t('Logins and registrations per month')}>
              <StackedBars rows={rep.series} keys={peopleKeys} ariaLabel={t('Stacked bars of members who logged in and registrations per month.')} />
            </Section>
          </div>
          <div className="grid gap-5 lg:grid-cols-3">
            <Section title={t('Recent actions')}>
              <Table
                head={[{ label: t('When') }, { label: t('Member') }, { label: t('Action') }]}
                rows={rep.activity.slice(0, 15).map((x) => [
                  <span className="whitespace-nowrap text-xs text-mms-muted">{when(x.when)}</span>,
                  x.name || (x.member != null ? `#${x.member}` : '—'),
                  <span className="inline-flex flex-col gap-0.5"><span className="text-sm text-mms-text">{x.label || x.kind}</span><ProductBadge product={x.product} /></span>])}
                empty={t('No actions in this range.')} />
            </Section>
            <Section title={t('Logins')} right={<span className="text-xs text-mms-faint mms-num">{fmt(rep.logins_list.length)}</span>}>
              <Table
                head={[{ label: t('Day') }, { label: t('Member') }, { label: t('Logins'), right: true }]}
                rows={rep.logins_list.slice(0, 15).map((x) => [x.day, x.name, fmt(x.logins)])}
                empty={t('No logins in this range.')} />
            </Section>
            <Section title={t('Who registered')} right={<span className="text-xs text-mms-faint mms-num">{fmt(rep.registrations_list.length)}</span>}>
              <Table
                head={[{ label: t('Member') }, { label: t('Status') }, { label: t('Sponsor') }]}
                rows={rep.registrations_list.slice(0, 15).map((x) => [
                  <span>{x.name}<span className="block text-xs text-mms-faint">{x.membership_id}</span></span>,
                  <span className="text-xs">{t(x.status_label)}{x.tier_label ? ` · ${t(x.tier_label)}` : ''}</span>,
                  <span className="text-xs text-mms-muted">{x.sponsor_code || '—'}</span>])}
                empty={t('No registrations in this range.')} />
            </Section>
          </div>
          {rep.top_share_platforms.length > 0 && (
            <Section title={t('Where members share')}>
              <div className="flex flex-wrap gap-2">
                {rep.top_share_platforms.map((p) => (
                  <span key={p.platform} className="rounded-full border border-mms-border px-3 py-1 text-xs text-mms-muted">
                    {p.platform} <span className="mms-num text-mms-text">{fmt(p.count)}</span>
                  </span>
                ))}
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}
